import { useEffect, useMemo } from "react";
import {
  CircleMarker,
  MapContainer,
  Polyline,
  Popup,
  TileLayer,
  Tooltip as LeafletTooltip,
  useMap,
} from "react-leaflet";
import L, { LatLngBoundsExpression, LatLngExpression } from "leaflet";
import dayjs from "dayjs";
import "leaflet/dist/leaflet.css";
import { ILocationTrace } from "../types/tracking.types";

interface DriverTrailMapProps {
  traces: ILocationTrace[];
  height?: number;
}

const FitBounds = ({ bounds }: { bounds: LatLngBoundsExpression | null }) => {
  const map = useMap();
  useEffect(() => {
    if (!bounds) return;
    map.fitBounds(bounds, { padding: [24, 24], maxZoom: 16 });
  }, [bounds, map]);
  return null;
};

const pointColor = (idx: number, total: number, tripStatus?: string | null) => {
  if (idx === 0) return "#10b981"; // start - green
  if (idx === total - 1) return "#ef4444"; // end - red
  if (tripStatus && tripStatus.toUpperCase() === "STOPPED") return "#6b7280";
  return "#1677ff";
};

const DriverTrailMap = ({ traces, height = 320 }: DriverTrailMapProps) => {
  const positions = useMemo<LatLngExpression[]>(
    () => traces.map((t) => [t.latitude, t.longitude]),
    [traces]
  );

  const bounds = useMemo<LatLngBoundsExpression | null>(() => {
    if (traces.length === 0) return null;
    if (traces.length === 1) {
      const t = traces[0];
      return L.latLng(t.latitude, t.longitude).toBounds(500);
    }
    const lats = traces.map((t) => t.latitude);
    const lngs = traces.map((t) => t.longitude);
    return [
      [Math.min(...lats), Math.min(...lngs)],
      [Math.max(...lats), Math.max(...lngs)],
    ];
  }, [traces]);

  const center = useMemo<LatLngExpression>(() => {
    if (traces.length === 0) return [20.5937, 78.9629]; // India fallback
    const t = traces[Math.floor(traces.length / 2)];
    return [t.latitude, t.longitude];
  }, [traces]);

  return (
    <div
      style={{
        border: "1px solid #e2e8f0",
        borderRadius: 10,
        overflow: "hidden",
        marginBottom: 16,
      }}
    >
      <MapContainer
        center={center}
        zoom={13}
        style={{ height, width: "100%" }}
        scrollWheelZoom
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <FitBounds bounds={bounds} />

        {positions.length > 1 && (
          <Polyline
            positions={positions}
            pathOptions={{ color: "#1677ff", weight: 3, opacity: 0.7 }}
          />
        )}

        {traces.map((t, idx) => {
          const color = pointColor(idx, traces.length, t.tripStatus);
          const isEdge = idx === 0 || idx === traces.length - 1;
          return (
            <CircleMarker
              key={`${t.timestamp}-${idx}`}
              center={[t.latitude, t.longitude]}
              radius={isEdge ? 7 : 5}
              pathOptions={{
                color,
                fillColor: color,
                fillOpacity: 0.85,
                weight: isEdge ? 2 : 1,
              }}
            >
              <LeafletTooltip direction="top" offset={[0, -6]} opacity={1}>
                <div style={{ fontSize: 12 }}>
                  <div style={{ fontWeight: 600 }}>
                    {dayjs(t.timestamp).format("MMM D, h:mm:ss A")}
                  </div>
                  {typeof t.speed === "number" && (
                    <div style={{ color: "#64748b" }}>
                      {t.speed.toFixed(1)} km/h
                      {t.tripStatus ? ` · ${t.tripStatus}` : ""}
                    </div>
                  )}
                </div>
              </LeafletTooltip>
              <Popup>
                <div style={{ fontSize: 12, maxWidth: 240 }}>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>
                    {idx === 0
                      ? "Start · "
                      : idx === traces.length - 1
                      ? "Latest · "
                      : `Point ${idx + 1} · `}
                    {dayjs(t.timestamp).format("MMM D, h:mm:ss A")}
                  </div>
                  <div style={{ color: "#475569", marginBottom: 4 }}>
                    {t.detailedAddress ||
                      `${t.latitude.toFixed(5)}, ${t.longitude.toFixed(5)}`}
                  </div>
                  <div style={{ color: "#64748b", fontSize: 11 }}>
                    {typeof t.speed === "number" && (
                      <>Speed: {t.speed.toFixed(1)} km/h</>
                    )}
                    {t.tripStatus && <> · {t.tripStatus}</>}
                  </div>
                </div>
              </Popup>
            </CircleMarker>
          );
        })}
      </MapContainer>
      <div
        style={{
          padding: "6px 10px",
          background: "#f8fafc",
          borderTop: "1px solid #e2e8f0",
          fontSize: 11,
          color: "#64748b",
          display: "flex",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 8,
        }}
      >
        <span>
          <span
            style={{
              display: "inline-block",
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: "#10b981",
              marginRight: 4,
            }}
          />
          Start
          <span style={{ marginLeft: 10 }}>
            <span
              style={{
                display: "inline-block",
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: "#1677ff",
                marginRight: 4,
              }}
            />
            Trace point
          </span>
          <span style={{ marginLeft: 10 }}>
            <span
              style={{
                display: "inline-block",
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: "#6b7280",
                marginRight: 4,
              }}
            />
            Stopped
          </span>
          <span style={{ marginLeft: 10 }}>
            <span
              style={{
                display: "inline-block",
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: "#ef4444",
                marginRight: 4,
              }}
            />
            Latest
          </span>
        </span>
        <span>{traces.length} point{traces.length === 1 ? "" : "s"}</span>
      </div>
    </div>
  );
};

export default DriverTrailMap;
